import React, { useState, useEffect } from "react";
import Tooltip from "@mui/material/Tooltip";
import WarningIcon from "@mui/icons-material/Warning";
import { pink } from "@mui/material/colors";
import { computeBarcodeWithCheckDigit } from "../../CommonFuntion/BarCodeFun"; // already imported

const TableBarCode = ({
  element,
  tableName,
  tdkeys,
  name,
  values,
  setFieldValue,
  error,
  errorBorder,
  id,
  rowkeys,
  disabled,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setFieldValue(`${tableName}[${tdkeys}].${name}`, newValue);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const current = values?.[tableName]?.[tdkeys]?.[name];
    if (current) {
      const computed = computeBarcodeWithCheckDigit(
        current,
        element.type,
        element.calculation,
        element.size
      );
      if (computed && computed !== current) {
        setFieldValue(`${tableName}[${tdkeys}].${name}`, computed);
      }
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  // Auto-append check digit(s) when data is loaded via BindResultValue (initial populate)
  const currentValue = values?.[tableName]?.[tdkeys]?.[name] || "";
  useEffect(() => {
    if (!currentValue) return;
    if (isFocused) return; // do not modify while user is editing
    // Skip when calculation = "2" (Ignore) or "3" (Override) unless a digit is missing
    if (element.calculation === "2") return;
    const computed = computeBarcodeWithCheckDigit(
      currentValue,
      element.type,
      element.calculation,
      element.size
    );
    if (computed && computed !== currentValue) {
      setFieldValue(`${tableName}[${tdkeys}].${name}`, computed);
    }
  }, [
    currentValue,
    element.type,
    element.calculation,
    element.size,
    isFocused,
    tdkeys,
    tableName,
    name,
    setFieldValue,
  ]);

  let strCharactersText;
  let strHelpText;

  if (
    element.calculation === "3" ||
    element.type === "4" ||
    element.type === "5" ||
    element.type === "8" ||
    element.type === "9"
  ) {
    strCharactersText = "No. Characters: " + element.size;
  } else {
    strCharactersText = "Max. Characters: " + element.size;
  }

  switch (element.type) {
    case "0":
      strHelpText =
        "Code 39 - Full ASCII, " +
        strCharactersText +
        ", Valid characters: Alphanumeric '-', '.', ' ', '%', '$', '/', '+'";
      break;
    case "1":
      strHelpText =
        "Code 39 - Normal, " +
        strCharactersText +
        ", Valid characters: Alphanumeric '-', '.', ' ', '%', '$', '/', '+'";
      break;
    case "2":
      strHelpText =
        "Code 93, " +
        strCharactersText +
        ", Valid characters: Alphanumeric '-', '.', ' ', '%', '$', '/', '+', '(', ')'";
      break;
    case "3":
      strHelpText =
        "Code 128, " + strCharactersText + ", Valid characters: All Characters";
      break;
    case "4":
      strHelpText =
        "EAN-8, " + strCharactersText + ", Valid characters: Numeric";
      break;
    case "5":
      strHelpText =
        "EAN-13, " + strCharactersText + ", Valid characters: Numeric";
      break;
    case "6":
      strHelpText =
        "EAN/UPC 128, " +
        strCharactersText +
        ", Valid characters: All Characters";
      break;
    case "7":
      strHelpText =
        "Interleaved 2 of 5, " +
        strCharactersText +
        ", Numeric: All Characters";
      break;
    case "8":
      strHelpText =
        "UPC-A, " + strCharactersText + ", Valid characters: Numeric";
      break;
    case "9":
      strHelpText =
        "UPC-E, " + strCharactersText + ", Valid characters: Numeric";
      break;
    default:
      break;
  }

  switch (element.calculation) {
    case "0":
      strHelpText = strHelpText + ", Check digit: Validated";
      break;
    case "1":
      strHelpText = strHelpText + ", Check digit: Calculated";
      break;
    case "2":
      strHelpText = strHelpText + ", Check digit: Ignore";
      break;
    case "3":
      strHelpText = strHelpText + ", Check digit: Override";
      break;
    default:
      break;
  }

  return (
    <div className="wrapper">
      <div className="input-container">
        <input
          maxLength={
            parseInt(element.size) 
          }
          title={
            strHelpText +
            (element.validation?.pictureMask
              ? "\nPicture Mask:" + element.validation.pictureMask
              : "") +
            (element.validation?.mandatory ? "\nMandatory" : "")
          }
          type="text"
          className={`tableInput ${errorBorder}`}
          id={id + tdkeys + 1}
          rowkeys={tdkeys}
          tdkeys={rowkeys}
          name={`${tableName}[${tdkeys}].${name}`}
          value={values?.[tableName]?.[tdkeys]?.[name] || ""}
          onBlur={handleBlur}
          onChange={handleInputChange}
          onFocus={handleFocus}
          data-testid="TableBarCode"
          style={{ color: error?.[name] || isFocused ? "black" : "white" }}
          disabled={disabled}
        />
        {error?.[name] && (
          <span className="icon">
            <Tooltip title={error[name]}>
              <WarningIcon sx={{ color: pink[500] }} />
            </Tooltip>
          </span>
        )}
      </div>
      <div>
        {/* Display the highlighted last digit */}
        {!error?.[name] && !isFocused && (
          <div className="highlighted-digit">
            {values?.[tableName]?.[tdkeys]?.[name]?.slice(0, -1)}
            <span className="highlight">
              {values?.[tableName]?.[tdkeys]?.[name]?.slice(-1)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TableBarCode;
